/**
 * Collection grid reveal-on-scroll.
 *
 * Cards and editorial promos fade up as they enter view, in short row-wise
 * bursts rather than one item at a time — a slow settle rather than a ripple.
 *
 * Three things make this less trivial than it looks:
 * - `html`/`body` are `overflow: hidden` and `.page-wrapper` is the real scroll
 *   container at >=990px, so the observer needs `getIntersectionRoot()`.
 * - `amada-collection-stream` removes and re-inserts its promo `<li>`s whenever
 *   the product signature changes, so newly seen nodes must be picked up again.
 * - `paginated-list` appends pages via view transitions, so this must be
 *   idempotent and fire once per element.
 */

import { getIntersectionRoot } from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';

const GRID_SELECTOR = 'ul.product-grid[data-testid="product-grid"]';
const ITEM_SELECTOR = 'li.product-grid__item';
const READY_ATTR = 'data-amada-reveal';

/** Elements already handed to the observer. */
const tracked = new WeakSet();

/**
 * True once the first pass has run.
 *
 * The entrance animation is for arriving at a grid, not for changing one. A
 * facet update replaces every `<li>` with a new node, so nothing is in
 * `tracked` any more and the whole grid re-faded from zero — under the
 * shopper's eyes, on the part of the page they were already reading. After
 * boot, anything replaced inside the viewport appears immediately; anything
 * below the fold still animates on scroll, which keeps infinite-scroll
 * appends looking the way they were designed to.
 */
let booted = false;

/**
 * Is this element inside the scroll container's visible box right now?
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function inViewport(element) {
  const root = getIntersectionRoot();
  const bounds =
    root instanceof Element
      ? root.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight || 0 };
  const rect = element.getBoundingClientRect();
  return rect.bottom > bounds.top && rect.top < bounds.bottom;
}

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * Items entering together are staggered by their position in the row, so a row
 * arrives as one gesture instead of a left-to-right cascade.
 * @param {HTMLElement} element
 * @param {HTMLElement} grid
 * @returns {number} delay in milliseconds
 */
function rowDelay(element, grid) {
  const styles = getComputedStyle(grid);
  const columns = styles.gridTemplateColumns.split(/\s+/).filter(Boolean).length || 1;
  const index = Array.prototype.indexOf.call(grid.children, element);
  if (index < 0) return 0;
  const step = parseInt(styles.getPropertyValue('--amada-reveal-stagger'), 10) || 60;
  return (index % columns) * step;
}

/**
 * Reveal an element unconditionally. Used by the observer and by the failsafe.
 * @param {HTMLElement} element
 */
function reveal(element) {
  element.setAttribute(READY_ATTR, 'in');
}

/**
 * Nothing about a decorative fade is worth risking an invisible product grid.
 * If the observer has not reported on an element shortly after it was
 * registered — a detached scroll root, a hidden container, a browser that
 * never fires the callback — show it anyway.
 * @param {HTMLElement} element
 */
function failsafe(element) {
  setTimeout(() => {
    if (element.getAttribute(READY_ATTR) === 'out') {
      reveal(element);
      observer?.unobserve(element);
    }
  }, 1500);
}

function ensureObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const element = /** @type {HTMLElement} */ (entry.target);
        const grid = element.closest(GRID_SELECTOR);
        if (grid instanceof HTMLElement) {
          element.style.transitionDelay = `${rowDelay(element, grid)}ms`;
        }
        reveal(element);
        observer?.unobserve(element);
      }
    },
    {
      root: getIntersectionRoot(),
      // Start the transition slightly before the card reaches the fold so it
      // has settled by the time it is properly in view.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    }
  );

  return observer;
}

/**
 * Observe any grid items not yet handled. Safe to call repeatedly.
 * @param {ParentNode} [root]
 */
export function revealGridItems(root = document) {
  const grids = root.querySelectorAll?.(GRID_SELECTOR) ?? [];
  if (!grids.length) return;

  // With reduced motion the items are simply marked as revealed; the CSS then
  // renders them at rest with no transform and no transition.
  const reduced = prefersReducedMotion();

  for (const grid of grids) {
    grid.setAttribute('data-amada-reveal-grid', '');

    for (const item of grid.querySelectorAll(ITEM_SELECTOR)) {
      if (tracked.has(item)) continue;
      tracked.add(item);

      if (reduced || (booted && inViewport(item))) {
        reveal(item);
        continue;
      }

      item.setAttribute(READY_ATTR, 'out');
      ensureObserver().observe(item);
      failsafe(item);
    }
  }
}

/**
 * Watch for items added by infinite scroll, facet morphs and the editorial
 * stream re-intercalating its promo cells.
 */
function watchGrids() {
  let queued = false;
  const flush = () => {
    queued = false;
    revealGridItems();
  };
  const queue = () => {
    if (queued) return;
    queued = true;
    if ('requestIdleCallback' in window) window.requestIdleCallback(flush, { timeout: 400 });
    else window.setTimeout(flush, 50);
  };
  const mutations = new MutationObserver(queue);
  mutations.observe(document.body, { childList: true, subtree: true });
}

function init() {
  revealGridItems();
  watchGrids();
  booted = true;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// The editorial stream calls this after each intercalation pass.
window.amadaRevealGridItems = revealGridItems;
